import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { createApiClient } from '@shripos/ui';
import { useSession } from '../stores/session.js';

export const API_ORIGIN = import.meta.env.VITE_API_URL ?? '';
export const APP_VERSION = __APP_VERSION__;

export const api = createApiClient({
  baseUrl: `${API_ORIGIN}/api/v1`,
  onAuthLost: (err) => handleAuthError(err),
});

// License errors can come back from any request: switch to the lock / sign-in screen globally.
const onError = (err) => {
  if (['LICENSE_SUSPENDED', 'LICENSE_EXPIRED'].includes(err?.code)) handleAuthError(err);
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError }),
  mutationCache: new MutationCache({ onError }),
  defaultOptions: {
    queries: { staleTime: 15_000, retry: (n, err) => n < 2 && (err?.status ?? 500) >= 500 },
  },
});

/** Map license/session errors from any request onto the session state. */
export function handleAuthError(err) {
  const s = useSession.getState();
  if (err?.code === 'LICENSE_SUSPENDED') {
    s.locked({ code: err.code, reason: err.details?.reason ?? null });
  } else if (err?.code === 'LICENSE_EXPIRED') {
    queryClient.clear();
    s.signedOut('licenseExpired');
  } else if (s.status === 'authenticated') {
    queryClient.clear();
    s.signedOut('sessionEnded');
  }
}

export function qs(params) {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') s.set(k, v);
  const str = s.toString();
  return str ? `?${str}` : '';
}

/** Reload the signed-in profile (after license changes, outlet switch, etc.). */
export async function reloadMe() {
  try {
    const me = await api.get('/auth/me');
    useSession.getState().signedIn(me);
    return me;
  } catch (err) {
    handleAuthError(err);
    return null;
  }
}

export async function logout() {
  try {
    await api.post('/auth/logout');
  } catch {
    /* already gone */
  }
  queryClient.clear();
  useSession.getState().signedOut();
}
